import React from "react";
import { ModeRendererProps } from "./types";

// VASARELY × MONTBLANC: Thought geometry
// Ink symmetry, line flow, silver geometry
export function VasarelyMontblancMode({ brand }: ModeRendererProps) {
  const mbBlack = "#000000";
  const mbSilver = "#C0C0C0";

  return (
    <g>
      {/* Elegant dark background */}
      <rect x="0" y="0" width="794" height="1123" fill="#0A0A0A" />

      <g transform="translate(397 540)">
        {/* Ink flow waves */}
        {Array.from({ length: 14 }).map((_, i) => {
          const y = -280 + i * 40;
          const points = Array.from({ length: 50 }).map((_, j) => {
            const x = -300 + j * 12;
            const flow = Math.sin(j * 0.2 + i * 0.3) * 20;
            return `${x},${y + flow}`;
          }).join(" ");
          
          return (
            <polyline
              key={i}
              points={points}
              fill="none"
              stroke={mbSilver}
              strokeWidth="1.5"
              opacity={0.3}
            />
          );
        })}

        {/* Writing symmetry (六角星 - six-pointed star inspiration) */}
        {[0, 60, 120, 180, 240, 300].map((angle) => {
          const rad = (angle * Math.PI) / 180;
          const r = 200;
          
          return (
            <g key={angle}>
              <line
                x1="0"
                y1="0"
                x2={Math.cos(rad) * r}
                y2={Math.sin(rad) * r}
                stroke={mbSilver}
                strokeWidth="2"
                opacity="0.4"
              />
              <circle
                cx={Math.cos(rad) * r}
                cy={Math.sin(rad) * r}
                r="12"
                fill={mbSilver}
                opacity="0.6"
              />
            </g>
          );
        })}

        {/* Line geometry grid */}
        {Array.from({ length: 14 }).map((_, i) =>
          Array.from({ length: 18 }).map((_, j) => {
            const x = (i - 6.5) * 44;
            const y = (j - 8.5) * 42;
            
            return (
              <rect
                key={`${i}-${j}`}
                x={x - 18}
                y={y - 18}
                width="36"
                height="36"
                fill="none"
                stroke={mbSilver}
                strokeWidth="0.5"
                opacity="0.15"
              />
            );
          })
        )}

        {/* Precision circles */}
        {Array.from({ length: 16 }).map((_, i) => {
          const r = 40 + i * 15;
          return (
            <circle
              key={i}
              cx="0"
              cy="0"
              r={r}
              fill="none"
              stroke={mbSilver}
              strokeWidth="1"
              opacity={0.3 - i * 0.016}
            />
          );
        })}

        {/* Thought nodes (intellectual design) */}
        {[0, 72, 144, 216, 288].map((angle) => {
          const rad = (angle * Math.PI) / 180;
          const r = 170;
          
          return (
            <g key={angle}>
              {/* Hexagon (Montblanc emblem) */}
              {Array.from({ length: 6 }).map((_, k) => {
                const hAngle = angle + k * 60;
                const hRad = (hAngle * Math.PI) / 180;
                const nextHRad = ((angle + (k + 1) * 60) * Math.PI) / 180;
                const hSize = 14;
                
                return (
                  <line
                    key={k}
                    x1={Math.cos(rad) * r + Math.cos(hRad) * hSize}
                    y1={Math.sin(rad) * r + Math.sin(hRad) * hSize}
                    x2={Math.cos(rad) * r + Math.cos(nextHRad) * hSize}
                    y2={Math.sin(rad) * r + Math.sin(nextHRad) * hSize}
                    stroke={mbSilver}
                    strokeWidth="2"
                    opacity="0.7"
                  />
                );
              })}
              <circle
                cx={Math.cos(rad) * r}
                cy={Math.sin(rad) * r}
                r="6"
                fill={brand.white}
                opacity="0.8"
              />
            </g>
          );
        })}

        {/* Central star (Meisterstück) */}
        {Array.from({ length: 6 }).map((_, i) => {
          const angle = i * 60;
          const rad = (angle * Math.PI) / 180;
          const nextRad = ((angle + 60) * Math.PI) / 180;
          const r = 50;
          
          return (
            <g key={i}>
              <path
                d={`M 0 0 L ${Math.cos(rad) * r} ${Math.sin(rad) * r} L ${Math.cos(nextRad) * r} ${Math.sin(nextRad) * r} Z`}
                fill={mbSilver}
                opacity="0.4"
              />
              <line
                x1="0"
                y1="0"
                x2={Math.cos(rad) * r}
                y2={Math.sin(rad) * r}
                stroke={brand.white}
                strokeWidth="2"
                opacity="0.6"
              />
            </g>
          );
        })}
        
        <circle cx="0" cy="0" r="50" fill="none" stroke={brand.white} strokeWidth="3" opacity="0.8" />
        <circle cx="0" cy="0" r="15" fill={brand.white} opacity="0.9" />
      </g>

      {/* Montblanc text */}
      <text x="397" y="1020" textAnchor="middle" fontFamily="serif" fontSize="11" fill={mbSilver} opacity="0.7" fontStyle="italic">
        géométrie de la pensée
      </text>
    </g>
  );
}
